import { Component, OnInit, TemplateRef, OnDestroy, ElementRef, inject, ChangeDetectionStrategy, viewChild } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { RegistrationService } from '../../services/registration-service.service';

import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SignInService } from '../../services/sign-in-service';
import { CitadelNotificationService } from '@citadel/core';
import { RecaptchaService } from 'src/app/services/recaptcha.service';
import { CountryModel } from 'src/app/models/country.model';
import { ComponentFSPDetailDTO } from 'src/app/models/component-fsp-detail.model';

/** Shape of citadel-phone-number-input's control value (its own CitadelPhoneNumberValue is not
 * exported from @citadel/controls, so this mirrors it structurally). */
interface PhoneNumberValue {
  countryL?: number | string;
  dialingCode?: number | string;
  number?: string;
}

@Component({
  selector: 'app-registration-page',
  templateUrl: './registration-page.component.html',
  styleUrls: ['./registration-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RegistrationPageComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private registration = inject(RegistrationService);
  private recaptchaService = inject(RecaptchaService);
  private notify = inject(CitadelNotificationService);
  private signInService = inject(SignInService);

  readonly identification = viewChild<TemplateRef<any>>('identification');
  readonly otpchannel = viewChild<TemplateRef<any>>('otpchannel');
  readonly enterotp = viewChild<TemplateRef<any>>('enterotp');
  readonly password = viewChild<TemplateRef<any>>('password');
  readonly completeregistration = viewChild<TemplateRef<any>>('completeregistration');
  country?: number | string;
  readonly otpInput = viewChild<ElementRef>('otpInput');

  selectedIndex = 0;
  steps: any = [
    { title: new BehaviorSubject<string>('Personal Details') },
    { title: new BehaviorSubject<string>('Password') },
    { title: new BehaviorSubject<string>('One-time-PIN') }
  ];

  // Set only from the viewChild signals below, each of which is `TemplateRef<any> | undefined`
  // until its template exists in the DOM.
  currentTemplate?: TemplateRef<any>;
  // Both set synchronously in ngOnInit (setPasswordForm/setIdentityForm) before Angular's first
  // change-detection pass can read them.
  passwordForm!: FormGroup;
  identityForm!: FormGroup;
  countries: CountryModel[] = [];
  tempcountries: CountryModel[] = [];
  // Only assigned in the constructor when there is a current navigation.
  extras?: NavigationExtras;
  // Left `any`: pre-existing bug - never assigned anywhere in this component, so always undefined
  // at every read (saveNewUser's 4th arg, the router.navigate state below).
  returnUrl: any;
  // Carries SignInService.registrationReturnUrl forward, which is itself unset until some other
  // page sets it.
  redirectUrl: string | undefined;
  // Left `any`: same bug as returnUrl - never assigned, always undefined at its two reads.
  appID: any;
  clientId?: string;
  registerMessage?: string;
  defaultErrorMessage: string = 'An error occured';
  clientName?: string;
  requestId: string = '';
  passwordMessage?: string;
  // Mirrors RegistrationService's own field, which is itself only set once
  // validatePasswordStrength has run.
  passwordStrength?: string;
  confirmPassword?: string;
  cellNumber: string = '';
  idNumber: string = '';
  emailAddress: string = '';
  countryCode: string = '+27';
  pwd?: string;
  otpType?: string;
  otpValue: string = '';
  score: number = 0;
  recaptchav3_token?: string;
  termsAndConditions?: boolean;
  otpSent?: boolean;
  switchBullet?: boolean;
  hasUpperAndLower: boolean | null = null;
  hasMinLength: boolean | null = null;
  hasNumber: boolean | null = null;
  hasSymbol: boolean | null = null;
  bar0?: string;
  bar1?: string;
  bar2?: string;
  scoreColor?: string;
  passwordMatch?: boolean;
  initialPhone: PhoneNumberValue = { countryL: 0, dialingCode: '', number: '' };
  //togglePhoneNumber: boolean = true;

  //initialPhone: any = { countryL: "48", dialingCode: "44", number: '' } //Guernsey PWL

  isConfirmPasswordPristine: boolean = true;
  otpTypeLabels = [
    { label: "SMS", value: "SMS" },
    { label: "WhatsApp", value: "WhatsApp" }
  ];

  private unsubscribe$ = new Subject<void>();
  componentFSPDetailsArray: ComponentFSPDetailDTO[] = [];
  conditionUrl: string = '';

  // convenience getter for easy access to form fields
  get idForm() {
 return this.identityForm.controls; 
}
  get pswForm() {
 return this.passwordForm.controls; 
}
  identityFormSubmitted: boolean = false;
  passwordFormSubmitted: boolean = false;
  showPinRequiredMsg: boolean = false;
  showChannelRequiredMsg: boolean = false;
  countryAllList: CountryModel[] = [];

  get otpTypeLabel(): string {
    return this.otpType === 'SMS' ? 'phone messaging app' : this.otpType === 'WhatsApp' ? 'WhatsApp' : '';
  }

  constructor() {
    const router = this.router;

    const navigation = router.currentNavigation();
    if (navigation) {
      this.extras = navigation.extras;
    }
    this.redirectUrl = this.signInService.registrationReturnUrl;
  }

  ngOnInit(): void {
    combineLatest<[CountryModel[], ComponentFSPDetailDTO[]]>([
      this.registration.getCountries(),
      this.registration.getComponentFSPDetail()
    ]).subscribe((data) => {

      this.countryAllList = data[0];

      //this.countries = data[0];
      this.componentFSPDetailsArray = data[1];
      //this.setIdentityForm();
      this.determineFeatures(this.componentFSPDetailsArray);

    });
    this.setIdentityForm();
    this.setPasswordForm();

    // setTimeout(() => {
    //   this.otpType = 'SMS';

    // this.currentTemplate = this.enterotp;
    // },1);
  }

  determineFeatures(details: ComponentFSPDetailDTO[]) {
    if (details && details.length > 0) {
      let fIndx = details.findIndex(d => d.componentFSPDetailSectionL === 1 && d.paramKeyL === 10);
      if (fIndx !== -1) {
        this.conditionUrl = details[fIndx].paramValue;
      }

      let country = '';
      let dialingCode = '';
      let tempcountyEntry: CountryModel[] = [];
      const countryNumberList: number[] = [];
      const tempcountries: CountryModel[] = [];
      let firstCountry = 0;

      const filterAll = details.filter(d => d.componentFSPDetailSectionL === 11 && d.paramKeyL === 17);
      if (filterAll.length > 0) {

        for (let i = 0; i < filterAll.length; i++) {
          tempcountyEntry = [];
          country = filterAll[i].paramValue;

          const countryNumber = parseInt(country);
          if (i === 0) {
            firstCountry = countryNumber;
          } 
          if (countryNumber > 0) {
            countryNumberList.push(countryNumber);
            tempcountyEntry = this.countryAllList.filter(c => c.lid === countryNumber);
            tempcountries.push(...tempcountyEntry);
          }
        }

        tempcountries.push(...this.countryAllList.filter(c => !countryNumberList.includes(c.lid)));
        this.countries = tempcountries;

      } else {
        this.countries = this.countryAllList;
      }

      fIndx = details.findIndex(d => d.componentFSPDetailSectionL === 11 && d.paramKeyL === 18);
      if (fIndx !== -1) {
        dialingCode = details[fIndx].paramValue;
      }

      if (firstCountry !== 0 && dialingCode !== '') {

        setTimeout(() => {

          this.initialPhone = { countryL: firstCountry, dialingCode: dialingCode, number: '' };
          this.identityForm.controls['phoneNumber'].setValue(this.initialPhone);
          this.identityForm.controls['phoneNumber'].updateValueAndValidity();
        }, 1);

      }

    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  setPasswordForm(): void {
    this.passwordForm = this.formBuilder.group(
      {
        password: ['', Validators.required],
        confirmPassword: ['', Validators.required]
      });
  }

  checkInput(ev: Event) {
    const control = this.passwordForm.controls['password'];
    this.isConfirmPasswordPristine = false;
    if (control.value === (ev.target as HTMLInputElement).value) {
      this.passwordMatch = true;
    } else {
      this.passwordMatch = false;
    }
  }

  setIdentityForm(): void {
    this.identityForm = this.formBuilder.group({
      idNumber: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: [this.initialPhone, Validators.required],
      termsAndConditions: [false, Validators.requiredTrue],
    });
  }

  updateIdentification(e: Event, id: string): void {
    if (id === 'email') {
      (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.replace(/ /g, '');
    }
  }

  // Not read for its shape - only tested for truthiness.
  onOtpChannelChange(e: unknown): void {
    if (e && this.otpType) {
      this.showChannelRequiredMsg = false;
    }
  }

  updateOtp(e: string): void {
    if (e) {
      this.showPinRequiredMsg = false;
    }
    this.otpValue = e;
  }

  updatePassword(e: Event, password: string): void {
    this.checkPasswordStrength(e, password);
  }

  checkPasswordStrength(e: Event, password: string): void {
    this.registration.validatePasswordStrength(password === 'password' ? (e.target as HTMLInputElement).value : this.pwd ?? '');
    this.passwordStrength = this.registration.passwordStrength;
    this.hasUpperAndLower = this.registration.hasUpperAndLower;
    this.hasMinLength = this.registration.hasMinLength;
    this.hasNumber = this.registration.hasNumber;
    this.hasSymbol = this.registration.hasSymbol;
    this.scoreColor = this.registration.scoreColor;
    this.bar0 = this.registration.bar0;
    this.bar1 = this.registration.bar1;
    this.bar2 = this.registration.bar2;
    this.switchBullet = this.registration.switchBullet;
  }

  goToSendOTP(): void {
    this.passwordFormSubmitted = true;
    // stop here if form is invalid
    if (this.passwordForm.invalid) {
      return;
    }

    if (this.passwordMatch) {
      if (this.hasUpperAndLower && this.hasMinLength && this.hasNumber && this.hasSymbol && this.passwordMatch) {
        this.otpSent = false;
        this.selectedIndex = 2;
        this.currentTemplate = this.otpchannel();
      } else {
        this.notify.error("Please choose a strong password by following our guidelines on the screen.", "");
      }
    } else {
      this.notify.error("Passwords do not match.", "");
    }
  }

  goToEnterOTP(): void {
    if (!this.otpSent) {
      this.otpSent = true;
      if (this.otpType) {
        this.selectedIndex = 2;

        this.registration.sendOtp(this.otpType, this.requestId).subscribe(_ => {
          this.notify.success('OTP sent', "");
          this.currentTemplate = this.enterotp();
        }, _ => this.notify.error(this.defaultErrorMessage, ""));

      } else {
        this.showChannelRequiredMsg = true;
        this.notify.error('Please select an option before you continue', " ");
      }
    }
  }

  goToPassword(): void {
    this.identityFormSubmitted = true;
    // stop here if form is invalid
    if (this.identityForm.invalid) {
      this.notify.error('Please complete all fields.', '');
      return;
    }

    this.recaptchaService.execute('identifyClient').subscribe((token) => {
      this.recaptchav3_token = token;
      this.getIdentityFormValues();
    },
      () => {
        this.notify.error('Error verifying the request (reCaptcha v3)', " ");
      });
  }

  resendOTP(): void {
    this.otpSent = false;
    this.currentTemplate = this.otpchannel();
  }

  getIdentityFormValues(): void {
    this.idNumber = this.identityForm.controls.idNumber.value;
    this.emailAddress = this.identityForm.controls.email.value;
    this.cellNumber = this.identityForm.controls.phoneNumber.value.number;
    this.countryCode = this.identityForm.controls.phoneNumber.value.dialingCode;
    this.country = this.identityForm.controls.phoneNumber.value.countryL;
    // if (!this.idNumber || !this.emailAddress || !this.cellNumber) {
    //   this.notify.error('Please complete all fields.', " ", {
    //     toastClass: "toast-error"
    //   });
    // } 
    // else {
    if (this.identityForm.controls.termsAndConditions.value) {
      this.identifyClient();
    } else {
      this.notify.error('Please agree to the terms and conditions before you continue.', " ");
    }

    // }

  }

  identifyClient(): void {

    this.registration.identifyClient(this.idNumber, this.emailAddress, this.cellNumber, this.countryCode.toString(), (this.country ?? '').toString(), this.recaptchav3_token ?? '')
      .subscribe(details => {
        const res = details.split(';');
        if (res && res[1]) {
          this.clientName = res[1];
        }
        if (res && res.length > 1 && res[2]) {
          this.requestId = res[2];
        }
        if (res && res[0] && !res[0].match(/00000000-0000-0000-0000-000000000000/)) {
          if (res[0].match(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)) {
            this.clientId = res[0];
            this.clientId = this.clientId.replace(/\"/g, '');
            this.selectedIndex = 1;
            this.currentTemplate = this.password();

          } else {
            this.notify.error(res[0], " ");
          }
        } else if (res[0] && res[0].match(/00000000-0000-0000-0000-000000000000/)) {
          this.notify.error('The client could not be verified.', " ");
        } else {
          this.notify.error('The user already exists.', " ");
        }
      }, _error => {
        this.notify.error('The client could not be verified.', " ");
      });
  }

  completeRegistration(): void {
    if (!this.otpValue || this.otpValue.length < 4) {
      if (this.otpValue.length === 0) {
        this.showPinRequiredMsg = true;
      }
      this.notify.error('A 4-digit OTP is required', " ");
    } else {
      this.showPinRequiredMsg = false;
      this.saveUser();
    }
  }

  saveUser() {
    const password = this.passwordForm.value.password;
    this.registration.saveNewUser(this.otpValue, password, this.clientId ?? '', this.appID).subscribe(res => {
      this.registerMessage = res.toString();
      if (this.registerMessage.match(/success/)) {
        this.notify.success(this.registerMessage, " ");
        this.currentTemplate = this.completeregistration();
        setTimeout(() => {
          this.router.navigate(['sign-in'], { state: { returnUrl: this.redirectUrl, appID: this.appID, name: '', userName: this.emailAddress } });
        }, 3000);

      } else {
        this.notify.error(this.registerMessage, " ");
      }
    }, _ => {
      this.notify.error('An error occurred.', " ");
    });
  }

  handleKeyUp(e: KeyboardEvent, step: string) {
    if (e.keyCode === 13) {
      switch (step) {
        case "goToEnterOTP":
          this.goToEnterOTP();
          break;
        case "completeRegistration":
          this.completeRegistration();
          break;
      }
    }
  }
}

